import React from 'react';
import { Alert, Badge, Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import {
  buildRequirementOptions,
  getParcelId,
  getParcelLabel,
} from './negotiationWizardUtils';

const SelectCard = ({
  title,
  subtitle,
  selected = false,
  disabled = false,
  onClick,
}) => (
  <button
    type='button'
    disabled={disabled}
    onClick={disabled ? undefined : onClick}
    className={`w-100 text-start border rounded p-3 bg-white ${
      selected ? 'border-primary shadow-sm' : 'border-light'
    } ${disabled ? 'opacity-50' : ''}`}
    style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
  >
    <div className='d-flex justify-content-between align-items-start gap-2'>
      <div>
        <div className='fw-semibold text-dark fs-13'>{title}</div>
        {subtitle ? (
          <div className='text-muted fs-12 mt-1'>{subtitle}</div>
        ) : null}
      </div>
      {selected ? (
        <Badge color='primary' className='fs-11'>
          Dipilih
        </Badge>
      ) : null}
    </div>
  </button>
);

const LockedCargoSummary = ({ entry, options = [] }) => {
  const requirement = options.find(
    option => option.requirementIndex === entry?.requirementIndex
  );
  return (
    <>
      <p className='text-muted fs-13 mb-3'>
        Negotiation untuk Delivery Unit ini sudah dimulai. Requirement dan Offer
        tidak bisa diubah dan akan disimpan apa adanya.
      </p>
      {requirement ? (
        <div className='mb-3'>
          <SelectCard
            title={requirement.label}
            subtitle={requirement.subLabel}
            selected
            disabled
          />
        </div>
      ) : null}
      <ul className='list-unstyled mb-0 fs-13'>
        {(entry?.offers ?? []).map(offer => (
          <li
            key={`${offer.procurementMitraId}-${offer.kind}-${offer.quantityIncotermCounterId ?? ''}`}
            className='py-1 border-bottom'
          >
            <div>
              <span className='fw-semibold text-dark'>
                {offer.sellerName || offer.companyName || offer.idOffer}
              </span>
              <div className='text-muted fs-12'>
                {offer.sellerName || offer.companyName ? offer.idOffer : null}
              </div>
            </div>
            {offer.kind === 'counter' ? (
              <Badge color='light' className='text-dark border ms-2 fs-11'>
                counter
              </Badge>
            ) : null}
          </li>
        ))}
      </ul>
    </>
  );
};

const NegotiationWizard = ({
  parcels = [],
  procurement,
  selectionByParcel = {},
  blockedRequirements = {},
  onSelectRequirement,
  renderOfferTable,
}) => {
  if (!parcels.length) {
    return (
      <Alert color='light' className='mb-0 border'>
        Belum ada Delivery Unit pada pengadaan ini.
      </Alert>
    );
  }

  return (
    <div>
      <p className='text-muted fs-13 mb-3'>
        Lengkapi setiap Delivery Unit: pilih satu Requirement, lalu pilih
        minimal satu Offer. Semua Delivery Unit wajib dilengkapi sebelum pilihan
        disimpan.
      </p>

      {parcels.map(parcel => {
        const parcelId = getParcelId(parcel);
        const parcelKey = String(parcelId);
        const options = buildRequirementOptions(parcel, procurement);
        const selectedRequirementIndex =
          selectionByParcel[parcelKey]?.requirementIndex ?? null;
        const offerCount = selectionByParcel[parcelKey]?.offers?.length ?? 0;
        // Cargo already started in Negotiation: read-only, resent unchanged.
        const locked = Boolean(selectionByParcel[parcelKey]?.locked);
        const showOffers =
          !locked &&
          selectedRequirementIndex != null &&
          typeof renderOfferTable === 'function';

        return (
          <Card key={parcelKey} className='border shadow-none mb-4'>
            <CardHeader className='bg-light border-bottom p-3 d-flex flex-wrap justify-content-between align-items-center gap-2'>
              <h6 className='mb-0 text-dark fw-bold fs-14'>
                Delivery Unit : {getParcelLabel(parcel)}
              </h6>
              <div className='d-flex flex-wrap gap-2'>
                {locked ? (
                  <Badge color='dark' className='fs-11'>
                    Sudah dimulai
                  </Badge>
                ) : null}
                {selectedRequirementIndex != null ? (
                  <Badge color='light' className='text-dark border fs-11'>
                    Requirement {selectedRequirementIndex + 1}
                  </Badge>
                ) : (
                  <Badge color='secondary' className='fs-11'>
                    Belum dipilih
                  </Badge>
                )}
                {offerCount > 0 ? (
                  <Badge color='info' className='fs-11'>
                    {offerCount} Offer dipilih
                  </Badge>
                ) : selectedRequirementIndex != null ? (
                  <Badge color='warning' className='fs-11'>
                    Belum ada Offer
                  </Badge>
                ) : null}
              </div>
            </CardHeader>
            <CardBody className='p-3'>
              {locked ? (
                <LockedCargoSummary
                  entry={selectionByParcel[parcelKey]}
                  options={options}
                />
              ) : !options.length ? (
                <p className='text-muted fs-13 mb-0'>
                  Tidak ada Requirement untuk Delivery Unit ini.
                </p>
              ) : (
                <>
                  <p className='text-muted fs-13 mb-3'>
                    Pilih satu Requirement untuk Delivery Unit ini.
                  </p>
                  <Row className='g-3'>
                    {options.map(option => {
                      const blocked = Boolean(
                        blockedRequirements[
                          `${parcelId}:${option.requirementIndex}`
                        ]
                      );
                      return (
                        <Col key={option.requirementIndex} md={6}>
                          <SelectCard
                            title={option.label}
                            subtitle={
                              blocked ? 'Tidak ada ID Offer' : option.subLabel
                            }
                            selected={
                              !blocked &&
                              selectedRequirementIndex ===
                                option.requirementIndex
                            }
                            disabled={blocked}
                            onClick={() =>
                              onSelectRequirement(
                                parcelId,
                                option.requirementIndex
                              )
                            }
                          />
                        </Col>
                      );
                    })}
                  </Row>

                  {showOffers ? (
                    <div className='mt-4 pt-3 border-top'>
                      <p className='text-muted fs-13 mb-3'>
                        Pilih Offer mitra yang akan dibawa ke Negotiation
                        (minimal satu).
                      </p>
                      {renderOfferTable({
                        parcelId,
                        requirementIndex: selectedRequirementIndex,
                      })}
                    </div>
                  ) : null}
                </>
              )}
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
};

export default NegotiationWizard;
